import { Browser } from '@capacitor/browser';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { auth, callFirebaseFunction, fbConfig } from 'boot/firebase';
import { CapacitorFirebaseMFA } from 'capacitor-firebase-mfa';
import VerifyAuthenticatorAppMfa from 'components/Users/Modals/VerifyAuthenticatorAppMfa.vue';
import VerifyPhoneNumberMfa from 'components/Users/Modals/VerifyPhoneNumberMfa.vue';
import {
  fetchSignInMethodsForEmail,
  getMultiFactorResolver,
  GoogleAuthProvider,
  OAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  signInWithCredential,
  signInWithCustomToken,
  signInWithPopup,
} from 'firebase/auth';
import { get, isEmpty, isObject } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { useAuth } from 'src/composables/auth';
import { AUTH_ERROR_CODE, LOGIN_METHOD, MFA_METHODS, SSO_PROVIDER } from 'src/constants/authorization';
import { promptDialog } from 'src/design-system';
import Interactions from 'src/mixins/Interactions';
import { auth as authStore } from 'src/store/auth';
import { region as regionStore } from 'src/store/region';
import AppUtils, { isValidEmail } from 'src/utils/AppUtils';
import { clearBackups } from 'src/utils/backupUtils';
import { logEvent } from 'src/utils/LogEvent';

/** @typedef {import('@capacitor-community/apple-sign-in').SignInWithAppleOptions} SignInWithAppleOptions */

const BUNDLE_ID = 'com.hirasmus.app';

export default {
  data() {
    return {
      microsoftAuthSpinner: false,
      microsoftTenantName: null,
      microsoftAppId: '',

      /** @type {GoogleAuthProvider | null} */
      gglProvider: null,
      googleAuthSpinner: false,
      googleCredentialsForMfa: null,

      /** @type {OAuthProvider | null} */
      appleProvider: null,
      appleAuthSpinner: false,
      /** @type {SignInWithAppleOptions} */
      appleAuthData: {
        clientId: BUNDLE_ID,
        redirectURI: fbConfig.FIREBASE_AUTH_CALLBACK_URL,
        scopes: 'email name',
      },

      isReAuthenticateProcessing: false,
    };
  },

  mixins: [Interactions],

  setup() {
    const { getLoginMethodName } = useAuth();
    return { getLoginMethodName };
  },

  created() {
    this.setProvidersSettings();
  },

  computed: {
    ...mapState(authStore, ['userDetails']),
    ...mapState(regionStore, ['region']),

    isCapacitorMode() {
      return process.env.MODE === 'capacitor';
    },

    isNativeApp() {
      return this.$q.platform.is.capacitor && (this.$q.platform.is.ios || this.$q.platform.is.android);
    },

    msRedirectURI() {
      const location = fbConfig.FUNCTIONS_LOCATION;
      const projectId = fbConfig.FIREBASE_PROJECT_ID;

      return `https://${location}-${projectId}.cloudfunctions.net/validateMicrosoftAuth`;
    },

    msLoginUri() {
      const requestOrigin = `https://login.microsoftonline.com/${this.microsoftTenantName}`;
      const rawQueryParams = {
        client_id: this.microsoftAppId,
        response_type: 'id_token',
        scope: 'openid',
        nonce: 42,
        response_mode: 'form_post',
        prompt: 'select_account',
        redirect_uri: this.msRedirectURI,
        ...(this.isNativeApp ? { state: 'native_app' } : {}),
      };
      const queryParams = new URLSearchParams(rawQueryParams);

      return `${requestOrigin}/oauth2/authorize?${queryParams.toString()}`;
    }, // msLoginUri()

  },

  methods: {
    ...mapActions(authStore, ['setPendingCredential', 'changeLoaderState']),
    ...mapActions(regionStore, ['setRegion']),

    setProvidersSettings() {
      // Make user need to select their account every time they log in with Google provider
      const customParameters = { prompt: 'select_account' };
      this.setAppleProvider(customParameters);

      if (!this.isCapacitorMode) {
        this.setGoogleProvider(customParameters);
      }
    }, // setProvidersSettings()

    setGoogleProvider(customParameters = {}) {
      this.gglProvider = new GoogleAuthProvider();
      this.gglProvider.setCustomParameters(customParameters);
    }, // setGoogleProvider()

    setAppleProvider(customParameters = {}) {
      this.appleProvider = new OAuthProvider(SSO_PROVIDER.APPLE);
      this.appleProvider.setCustomParameters(customParameters);
      this.appleProvider.addScope('email');
      this.appleProvider.addScope('name');
    }, // setAppleProvider()

    async onMicrosoftLogin(prefilledEmail) {
      const initialEmail = localStorage.getItem('microsoftEmail') || prefilledEmail || '';

      promptDialog(
        this.$t('login.microsoftAuthTitle'),
        this.$t('login.microsoftAuthEmailMessage'),
        async (email) => {
          this.microsoftAuthSpinner = true;
          try {
            const result = await callFirebaseFunction('findClinicSsoDataByEmail', {
              email,
              ssoProvider: SSO_PROVIDER.MICROSOFT,
            });

            if (!isObject(result?.data)) {
              this.notifyNegative(this.$t('login.notifyMicrosoftAuthError', { message: 'internal' }));
              return;
            }

            const {
              region,
              ssoData: clinicMicrosoftSsoData,
            } = result.data;

            if (region && region !== this.region) {
              await this.setRegion({
                region,
                context: this,
              });
            }

            if (!clinicMicrosoftSsoData) {
              this.notifyNegative(this.$t('login.notifyMicrosoftAuthError', { message: 'internal' }));
              return;
            }

            this.microsoftTenantName = clinicMicrosoftSsoData.tenantName;
            this.microsoftAppId = clinicMicrosoftSsoData.applicationId;
            localStorage.setItem('microsoftEmail', email);

            if (this.isNativeApp) {
              await Browser.open({
                url: this.msLoginUri,
                windowName: 'HiRasmus - Microsoft authentication',
                // Not 'popover': on iPadOS 27 a popover SFSafariViewController renders blank (HR-5009).
                presentationStyle: 'fullscreen',
              });
              return;
            }

            window.location.href = this.msLoginUri;
          } catch (err) {
            if (!navigator.onLine) {
              this.notifyNegative(this.$t('common.noInternetConnectionMessage'));
            } else {
              this.notifyNegative(this.$t('login.notifyMicrosoftAuthError', { message: err.message }));
            }

            console.error(err);
            logEvent(err, {
              context: {
                context_data: {
                  email,
                  ssoProvider: SSO_PROVIDER.MICROSOFT,
                },
              },
            });
          } finally {
            this.microsoftAuthSpinner = false;
            await clearBackups();
          }
        },
        {
          model: initialEmail,
          type: 'email',
          rules: [(val) => isValidEmail(val) || this.$t('login.inputEmailValidator')],
          lazyRules: true,
        },
      );
    }, // onMicrosoftLogin()

    /**
     * @param {string} token
     */
    async loginWithCustomToken(token) {
      return signInWithCustomToken(auth, token)
        .then((result) => {
          if (!isEmpty(result.user)) {
            localStorage.setItem('userAuthenticated', 'true');
            this.changeLoaderState(true);
          }

          return result;
        })
        .catch((err) => {
          this.$q.loading.hide();
          localStorage.removeItem('userAuthenticated');

          this.handleProviderAuthErrors(err, SSO_PROVIDER.MICROSOFT);
        });
    }, // loginWithCustomToken()

    /**
     * @return {Promise<UserCredential | void>}
     */
    async onGoogleLogin() {
      this.googleAuthSpinner = true;

      try {
        if (this.isCapacitorMode) {
          const userCredentials = await this.nativeGoogleLogin();
          this.changeLoaderState(true);
          return userCredentials;
        }

        return await this.providerSignInWithPopup(this.gglProvider);
      } catch (err) {
        console.error(err);
        logEvent(err);
      } finally {
        this.googleAuthSpinner = false;
        await clearBackups();
      }
    }, // onGoogleLogin()

    async reAuthenticateWithGoogle() {
      this.googleAuthSpinner = true;

      try {
        if (this.isCapacitorMode) {
          return await this.nativeGoogleReAuthenticate();
        }

        return await this.providerReauthenticateWithPopup(this.gglProvider);
      } catch (err) {
        console.error(err);
        logEvent(err);
      } finally {
        this.googleAuthSpinner = false;
      }
    }, // reAuthenticateWithGoogle()

    async nativeGoogleLogin() {
      await SocialLogin.logout({ provider: 'google' });
      const { result } = await SocialLogin.login({
        provider: 'google',
        options: {
          scopes: ['profile', 'email'],
          forceRefreshToken: false,
        },
      });

      this.setDataForNativeGoogleMfa(result);

      const googleAuthCredential = GoogleAuthProvider.credential(result.idToken);
      return signInWithCredential(auth, googleAuthCredential)
        .catch((err) => {
          localStorage.removeItem('userAuthenticated');
          this.handleProviderAuthErrors(err, SSO_PROVIDER.GOOGLE);
        });
    }, // nativeGoogleLogin()

    async nativeGoogleReAuthenticate() {
      await SocialLogin.logout({ provider: 'google' });
      const { result } = await SocialLogin.login({
        provider: 'google',
        options: {
          scopes: ['profile', 'email'],
          forceRefreshToken: false,
        },
      });

      this.setDataForNativeGoogleMfa(result);

      try {
        const googleAuthCredential = GoogleAuthProvider.credential(result.idToken);
        return await reauthenticateWithCredential(auth.currentUser, googleAuthCredential);
      } catch (err) {
        return this.handleProviderAuthErrors(err, SSO_PROVIDER.GOOGLE);
      }
    }, // nativeGoogleReAuthenticate()

    // APPLE AUTHORIZATION

    async onAppleLogin() {
      this.appleAuthSpinner = true;

      try {
        if (this.isCapacitorMode) {
          const userCredentials = await this.nativeAppleLogin();
          this.changeLoaderState(true);
          return userCredentials;
        }

        return await this.providerSignInWithPopup(this.appleProvider);
      } catch (err) {
        console.error(err);
        logEvent(err);
        return null;
      } finally {
        this.appleAuthSpinner = false;
        await clearBackups();
      }
    }, // onAppleLogin()

    async nativeAppleLogin() {
      const { response, rawNonce } = await this.nativeAppleAuthorize();
      const appleAuthCredentials = this.appleProvider.credential({
        idToken: response.identityToken,
        rawNonce,
      });

      return signInWithCredential(auth, appleAuthCredentials)
        .catch((err) => {
          localStorage.removeItem('userAuthenticated');
          this.handleProviderAuthErrors(err, SSO_PROVIDER.APPLE);
        });
    }, // nativeAppleLogin()

    async nativeAppleAuthorize() {
      // We have to create a random nonce string and use its hashed value in our authorization request
      const { nonce } = await CapacitorFirebaseMFA.createRandomNonce();
      /** @type {SignInWithAppleOptions} */
      const options = {
        ...this.appleAuthData,
        nonce: await AppUtils.hashString(nonce),
      };

      const { response } = await SignInWithApple.authorize(options);

      return { response, rawNonce: nonce };
    }, // nativeAppleAuthorize()

    async reAuthenticateWithApple() {
      this.appleAuthSpinner = true;
      this.isReAuthenticateProcessing = true;

      try {
        if (this.isCapacitorMode) {
          return await this.nativeAppleReAuthenticate();
        }

        return await this.providerReauthenticateWithPopup(this.appleProvider);
      } catch (err) {
        console.error(err);
        logEvent(err);
      } finally {
        this.appleAuthSpinner = false;
        this.isReAuthenticateProcessing = false;
      }
    }, // reAuthenticateWithApple()

    async nativeAppleReAuthenticate() {
      const { response, rawNonce } = await this.nativeAppleAuthorize();
      const appleAuthCredentials = this.appleProvider.credential({
        idToken: response.identityToken,
        rawNonce,
      });

      try {
        return await reauthenticateWithCredential(auth.currentUser, appleAuthCredentials);
      } catch (err) {
        return this.handleProviderAuthErrors(err, SSO_PROVIDER.APPLE);
      }
    }, // nativeAppleLogin()

    setDataForNativeGoogleMfa(result) {
      if (!result) {
        return;
      }

      this.gglProvider = { providerId: SSO_PROVIDER.GOOGLE };
      this.googleCredentialsForMfa = {
        idToken: result.idToken,
        accessToken: result.accessToken?.token,
      };
    }, // setDataForNativeGoogleMfa()

    async providerSignInWithPopup(provider) {
      try {
        const result = await signInWithPopup(auth, provider);
        // successful sign in
        if (!isEmpty(result.user)) {
          localStorage.setItem('userAuthenticated', 'true');
        }

        return result;
      } catch (err) {
        this.$q.loading.hide();
        localStorage.removeItem('userAuthenticated');
        const providerId = get(provider, 'providerId', '');
        return this.handleProviderAuthErrors(err, providerId);
      }
    }, // providerSignInWithPopup()

    async providerReauthenticateWithPopup(provider) {
      try {
        return await reauthenticateWithPopup(auth.currentUser, provider);
      } catch (err) {
        this.$q.loading.hide();

        const providerId = get(provider, 'providerId', '');
        return this.handleProviderAuthErrors(err, providerId);
      }
    }, // providerSignInWithPopup()

    handleProviderAuthErrors(error, providerId) {
      logEvent(error); // for some time, log all errors

      if (!navigator.onLine) {
        this.notifyNegative(this.$t('common.noInternetConnectionMessage'));
        return;
      }

      switch (error.code) {
        case AUTH_ERROR_CODE.EXISTS_WITH_DIFFERENT_CREDENTIAL:
          this.setPendingCredential(error.credential);

          fetchSignInMethodsForEmail(auth, error.email)
            .then((methods) => {
              if (methods[0] === 'password') {
                this.$router.push({
                  path: '/login',
                  query: { email: error.email },
                });
              }
            });

          break;

        case AUTH_ERROR_CODE.OPERATION_NOT_ALLOWED:
          /**
           * handle when user choose provider that is not enable in current region
           */
          this.$router.push({ name: 'region' }, () => {
            this.notifyWarning(this.$t('signup.signupComponent.notifyProviderNotAllowedInThisRegion'));
          });

          break;

        case AUTH_ERROR_CODE.USER_CANCELLED:
          /**
           * handle when user tries to log in with corporation microsoft account and gets warning
           * that they need to log in with administrator account of their corporate
           */
          this.changeMicrosoftUserRegion();
          break;

        case AUTH_ERROR_CODE.MULTI_FACTOR_REQUIRED:
          if (providerId === SSO_PROVIDER.GOOGLE) {
            return this.showMfaDialogForGoogleLogin(error);
          }
          if (providerId === SSO_PROVIDER.APPLE) {
            return this.showMfaDialogForAppleLogin(error);
          }
          break;

        case AUTH_ERROR_CODE.POPUP_CANCELLED_BY_USER:
        case AUTH_ERROR_CODE.POPUP_CLOSED_BY_USER:
          // user closed apple/google popup, so it's not an actual error
          this.googleAuthSpinner = false;
          this.appleAuthSpinner = false;
          this.changeLoaderState(false);
          return { cancelledRequest: true };

        case AUTH_ERROR_CODE.USER_MISMATCH:
          // User choose wrong account trying to reauthorize
          this.notifyNegative(this.$t('users.modals.reAuthenticate.emailIsNotMatch', {
            provider: this.getLoginMethodName(providerId) || this.getLoginMethodName(LOGIN_METHOD.PASSWORD),
          }));
          break;

        default:
          console.error(error);
          this.notifyNegative(error.message);
          logEvent(error);
      }
    }, // handleProviderAuthErrors()

    changeMicrosoftUserRegion() {
      let region;

      if (process.env.ENV_TYPE === 'production' && ['eu', 'us'].includes(this.region)) {
        region = this.region === 'eu' ? 'us' : 'eu';
      } else if (process.env.ENV_TYPE === 'development' && ['eu', 'ca'].includes(this.region)) {
        region = this.region === 'eu' ? 'ca' : 'eu';
      }

      if (region) {
        // used setTimeout to get around "firebase not initialize" error
        setTimeout(() => {
          this.setRegion({ region, context: this });
        }, 100);
        this.notifyNegative(this.$t('signup.signupComponent.notifyMicrosoftAdminSubmissionRequired'));
      }
    }, // changeMicrosoftUserRegion()

    showMfaDialogForGoogleLogin(error) {
      const resolver = getMultiFactorResolver(auth, error);
      const factorId = get(resolver, ['hints', 0, 'factorId']);
      const MfaComponent = factorId === MFA_METHODS.AUTHENTICATOR_APP.ID
        ? VerifyAuthenticatorAppMfa
        : VerifyPhoneNumberMfa;
      const mfaProps = factorId === MFA_METHODS.AUTHENTICATOR_APP.ID
        ? { resolver }
        : {
          resolver,
          provider: get(this.gglProvider, 'providerId', null),
          googleCredentials: this.googleCredentialsForMfa || null,
        };

      return new Promise((res) => {
        this.openComponentDialog(
          MfaComponent,
          mfaProps,
          (userCredential) => {
            localStorage.setItem('userAuthenticated', 'true');
            res(userCredential);
          },
          () => {
            this.googleAuthSpinner = false;
            this.changeLoaderState(false);
            res(null);
          },
        );
      });
    }, // showMfaDialogForGoogleLogin()

    showMfaDialogForAppleLogin(error) {
      const resolver = getMultiFactorResolver(auth, error);
      const factorId = get(resolver, ['hints', 0, 'factorId']);
      const MfaComponent = factorId === MFA_METHODS.AUTHENTICATOR_APP.ID
        ? VerifyAuthenticatorAppMfa
        : VerifyPhoneNumberMfa;
      const mfaProps = factorId === MFA_METHODS.AUTHENTICATOR_APP.ID
        ? { resolver }
        : {
          resolver,
          provider: get(this.appleProvider, 'providerId', null),
          appleAuthData: this.appleAuthData,
        };

      return new Promise((res) => {
        this.openComponentDialog(
          MfaComponent,
          mfaProps,
          (userCredential) => {
            localStorage.setItem('userAuthenticated', 'true');
            if (!this.isReAuthenticateProcessing) {
              this.changeLoaderState(true);
            }
            res(userCredential);
          },
          () => {
            this.appleAuthSpinner = false;
            this.changeLoaderState(false);
            res(null);
          },
        );
      });
    }, // showMfaDialogForAppleLogin()

  },
};
